/**
 * useDisputeAgent — Real API hook replacing useDisputeSimulator.
 *
 * Calls the BE dispute endpoints on the Invoice model.
 * Base URL: /api/v1/invoices/{invoice_id}/dispute/
 *
 * Uses SWR for data fetching with automatic polling when awaiting vendor response.
 */

import { useCallback, useRef } from 'react';
import useSWR, { type Fetcher, useSWRConfig } from 'swr';
import { z } from 'zod';

import { useAnalytics } from '~/analytics';
import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';
import {
  invoicesDisputeActionsList,
  invoicesDisputeCategorizeCreate,
  invoicesDisputeDraftDestroy,
  invoicesDisputeDraftUpdate,
  invoicesDisputeGenerateDraftCreate,
  invoicesDisputeInitiateCreate,
  invoicesDisputeReopenCreate,
  invoicesDisputeResolveCreate,
  invoicesDisputeRetrieve,
  invoicesDisputeSendCreate,
  invoicesDisputeTimelineList,
  invoicesDisputeWithdrawCreate,
} from '~/data/generated/endpoints/invoices/invoices';
import type {
  DisputeCategorizeRequest,
  DisputeResolveRequest,
  DraftUpdateRequest,
} from '~/data/generated/model';
import {
  InvoicesDisputeActionsListResponse,
  InvoicesDisputeRetrieveResponse,
  InvoicesDisputeTimelineListResponse,
} from '~/data/generated/zod/invoices/invoices.zod';
import { getInvoiceMutator } from '~/data/hooks/utils';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { parseApiResponse } from '~/data/parseApiResponse';
import type { Equal, Expect } from '~/data/typeAssertions';

import type {
  Attachment,
  AvailableAction,
  DisputeAgentState,
  EmailDraft,
  TimelineEntry,
} from './types';

function isNotFound(e: unknown): boolean {
  return e instanceof LiftAPIClientError && e.context.status === 404;
}

// `Invoice.dispute_current_draft` is a raw JSONField the API persists and
// echoes back verbatim (`_build_agent_state`, dispute/service.py:113-115) —
// it is never validated against `EmailDraftSerializer` server-side (that
// serializer is documentation-only, per its own module comment). `initiate_dispute`
// seeds it as just `{"from_email": DEFAULT_FROM_EMAIL}` (service.py:250-251)
// *before* `generate_draft`'s LLM call ever runs (service.py:257-260 calls
// `_build_agent_state` right after seeding, with no draft content yet); if
// that LLM call then fails/times out, this partial shape persists indefinitely.
// The generated `currentDraft` schema requires 7 keys this object doesn't have
// (draftType/subject/body/to/cc/isAgentGenerated/basedOnFindings) — without
// this override, every subsequent state fetch (including page reload) would
// throw and the dispute panel would be permanently stuck (AMP-593).
const disputeDraftSchema = InvoicesDisputeRetrieveResponse.shape.currentDraft.unwrap().extend({
  draftType: InvoicesDisputeRetrieveResponse.shape.currentDraft.unwrap().shape.draftType.optional(),
  subject: z.string().optional(),
  body: z.string().optional(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  isAgentGenerated: z.boolean().optional(),
  basedOnFindings: z.boolean().optional(),
});

export const disputeStateSchema = InvoicesDisputeRetrieveResponse.extend({
  currentDraft: disputeDraftSchema.nullable(),
});

const DEFAULT_STATE: DisputeAgentState = {
  invoiceId: '',
  status: 'none',
  initiatedAt: null,
  vendorPosition: null,
  followUpCount: 0,
  maxFollowUps: 3,
  nextFollowUpDate: null,
  daysSinceLastContact: 0,
  resolution: null,
  suggestedActions: [],
  attentionReason: '',
  currentDraft: null,
};

// Polling interval: 60 seconds when awaiting vendor response
const POLLING_INTERVAL_MS = 60_000;

// ── SWR Fetchers ─────────────────────────────────────────────────────────

const disputeStateFetcher: Fetcher<
  DisputeAgentState,
  readonly ['dispute', invoiceId: string]
> = async ([_, invoiceId]) => {
  try {
    const { data } = await invoicesDisputeRetrieve(invoiceId);
    const validated = parseApiResponse(disputeStateSchema, data, 'DisputeState');
    // Cast pinned by `_PinDisputeStateExceptAttachmentFileType` below — the one
    // gap is `currentDraft.attachments[].fileType` (AMP-620; see the pin for
    // why it can't be closed from the webapp).
    // eslint-disable-next-line local/no-parse-api-response-cast
    return validated as unknown as DisputeAgentState;
  } catch (e) {
    if (isNotFound(e)) {
      // No dispute exists yet — return default state
      return { ...DEFAULT_STATE, invoiceId };
    }
    throw e;
  }
};

const disputeTimelineFetcher: Fetcher<
  TimelineEntry[],
  readonly ['dispute', invoiceId: string, 'timeline']
> = async ([_, invoiceId]) => {
  try {
    const { data } = await invoicesDisputeTimelineList(invoiceId);
    const validated = parseApiResponse(
      InvoicesDisputeTimelineListResponse,
      data,
      'DisputeTimeline',
    );
    // Cast pinned by `_PinTimelineEntryExceptAttachmentFileType` below — same
    // one gap as the state fetcher above (AMP-620).
    // eslint-disable-next-line local/no-parse-api-response-cast
    return validated as unknown as TimelineEntry[];
  } catch (e) {
    if (isNotFound(e)) {
      return [];
    }
    throw e;
  }
};

const disputeActionsFetcher: Fetcher<
  AvailableAction[],
  readonly ['dispute', invoiceId: string, 'actions']
> = async ([_, invoiceId]) => {
  try {
    const { data } = await invoicesDisputeActionsList(invoiceId);
    const validated = parseApiResponse(InvoicesDisputeActionsListResponse, data, 'DisputeActions');
    return validated;
  } catch (e) {
    if (isNotFound(e)) {
      return [];
    }
    throw e;
  }
};

// ── AMP-620: pin the two remaining boundary casts above ─────────────────
//
// `disputeStateSchema`/`InvoicesDisputeTimelineListResponse` both embed the
// same `DisputeAttachmentSerializer` shape (services/api/main/views/api/dispute/
// serializers.py:88-95), which types `file_type` as a bare `CharField` instead
// of a `ChoiceField(choices=Attachment.FileType.choices)` — so drf-spectacular
// emits `fileType: string` instead of the model's real
// `'pdf' | 'image' | 'spreadsheet'` enum. That's the ONE gap between the
// parsed value and `DisputeAgentState`/`TimelineEntry[]`; every other field
// matches exactly. Fixing the serializer is a backend change (out of scope
// here); loosening `Attachment.fileType` to `string` on the frontend would
// break the narrower `AppAttachment.fileType` it's assigned to in
// `DisputeAgentPanel.tsx`. So the cast stays, scoped by these two pins: the
// first proves everything OTHER than `fileType` matches (so any other drift
// fails `tsc`); the second proves the gap is still exactly `string` (so the
// day the backend adds the `ChoiceField`, this flips to a compile error
// telling you to drop the cast).
type SchemaDraftAttachment = NonNullable<
  NonNullable<z.infer<typeof disputeStateSchema>['currentDraft']>['attachments']
>[number];
type SchemaTimelineAttachment = z.infer<
  typeof InvoicesDisputeTimelineListResponse
>[number]['attachments'][number];

type _PinDraftAttachmentExceptFileType = Expect<
  Equal<Omit<SchemaDraftAttachment, 'fileType'>, Omit<Attachment, 'fileType'>>
>;
type _PinTimelineAttachmentExceptFileType = Expect<
  Equal<Omit<SchemaTimelineAttachment, 'fileType'>, Omit<Attachment, 'fileType'>>
>;
type _PinFileTypeGapIsStillJustAString = Expect<Equal<SchemaDraftAttachment['fileType'], string>>;
type _PinTimelineFileTypeGapIsStillJustAString = Expect<
  Equal<SchemaTimelineAttachment['fileType'], string>
>;

export function useDisputeAgent(invoiceId: string | null) {
  const { mutate: globalMutate } = useSWRConfig();
  const { refresh: refreshActionItems } = useActionItemsRefresh();
  const { track } = useAnalytics();

  // Per-generation edit counter. Reset whenever `generateDraft` mints
  // a fresh draft; incremented whenever the user submits a content
  // edit through `updateDraft`. Consumed by `sendEmail` so the
  // dispute_email_sent.agent_generated property reflects whether the
  // *sent* version is unmodified-from-agent (true) or human-edited
  // (false), matching the draft-quality funnel diagram.
  const editsThisGenerationRef = useRef(0);

  // Helper to revalidate invoice data when dispute status changes
  const revalidateInvoice = useCallback(() => {
    if (invoiceId) {
      // Revalidate the invoice and the invoice table to pick up updated disputeStatus
      void globalMutate(getInvoiceMutator(invoiceId));
      // A dispute action may resolve the invoice's "dispute needs attention"
      // Backlog item, so refresh the action-items list and sidebar badge too.
      refreshActionItems();
    }
  }, [invoiceId, globalMutate, refreshActionItems]);

  // Determine if we should poll based on current state
  // We'll need to read state first to decide, so we use a callback for refreshInterval
  const {
    data: state,
    error: stateError,
    isLoading: stateLoading,
    mutate: mutateState,
  } = useSWR(invoiceId ? (['dispute', invoiceId] as const) : null, disputeStateFetcher, {
    // Poll every 60s when awaiting vendor response
    refreshInterval: (latestData) => {
      if (latestData?.status === 'awaiting-vendor') {
        return POLLING_INTERVAL_MS;
      }
      return 0; // No polling for other states
    },
  });

  const { data: timeline, mutate: mutateTimeline } = useSWR(
    invoiceId ? (['dispute', invoiceId, 'timeline'] as const) : null,
    disputeTimelineFetcher,
    {
      // Poll timeline alongside state when awaiting vendor
      refreshInterval: () => {
        if (state?.status === 'awaiting-vendor') {
          return POLLING_INTERVAL_MS;
        }
        return 0;
      },
    },
  );

  const { data: actions, mutate: mutateActions } = useSWR(
    invoiceId ? (['dispute', invoiceId, 'actions'] as const) : null,
    disputeActionsFetcher,
    {
      // Poll actions alongside state when awaiting vendor
      refreshInterval: () => {
        if (state?.status === 'awaiting-vendor') {
          return POLLING_INTERVAL_MS;
        }
        return 0;
      },
    },
  );

  const initiateDispute = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await invoicesDisputeInitiateCreate(invoiceId);
      track('dispute_initiated', { invoice_id: invoiceId });
    } catch (e) {
      throw e instanceof Error ? e : new Error('Failed to initiate dispute');
    }
  }, [invoiceId, track]);

  const generateDraft = useCallback(
    async (draftType: 'dispute' | 'data_request' | 'follow_up') => {
      if (!invoiceId) return;
      try {
        const { data } = await invoicesDisputeGenerateDraftCreate(invoiceId, { draftType });
        const draft = data as unknown as EmailDraft;
        // Merge draft into state
        await mutateState((prev) => (prev ? { ...prev, currentDraft: draft } : prev), {
          revalidate: false,
        });
        // New draft → reset the edit counter so any subsequent user
        // edits get attributed to *this* generation, not the previous
        // one, when sendEmail computes agent_generated.
        editsThisGenerationRef.current = 0;
        track('dispute_draft_generated', { invoice_id: invoiceId, draft_type: draftType });
      } catch (e) {
        throw e instanceof Error ? e : new Error('Failed to generate draft');
      }
    },
    [invoiceId, mutateState, track],
  );

  const updateDraft = useCallback(
    async (
      updates: Partial<
        Pick<EmailDraft, 'subject' | 'body' | 'to' | 'cc'> & { attachment_ids: string[] }
      >,
    ) => {
      if (!invoiceId) return;
      try {
        // The public param keeps the snake_case `attachment_ids` its callers
        // pass; map it onto the generated camelCase DraftUpdate body (AMP-503).
        const { attachment_ids, ...rest } = updates;
        const body: DraftUpdateRequest = {
          ...rest,
          ...(attachment_ids ? { attachmentIds: attachment_ids } : {}),
        };
        await invoicesDisputeDraftUpdate(invoiceId, body);
        await mutateState();
        // Only fire dispute_draft_edited for user-content edits, not the
        // attachment-ids piggyback that sendEmail performs before sending.
        const editedFields = (['subject', 'body', 'to', 'cc'] as const).filter((f) => f in updates);
        if (editedFields.length > 0) {
          editsThisGenerationRef.current += 1;
          track('dispute_draft_edited', { invoice_id: invoiceId, fields: editedFields });
        }
      } catch (e) {
        throw e instanceof Error ? e : new Error('Failed to update draft');
      }
    },
    [invoiceId, mutateState, track],
  );

  const discardDraft = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const { data } = await invoicesDisputeDraftDestroy(invoiceId);
      await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
      revalidateInvoice(); // Sync dispute status to invoice header
    } catch {
      // Fallback: refresh state and clear draft locally
      await mutateState((prev) => (prev ? { ...prev, currentDraft: null } : prev), {
        revalidate: true,
      });
    } finally {
      // Fire after the try/catch so the event reflects user intent
      // ("the user clicked Discard") whether the server cleared the
      // draft or the local fallback did. Either way the form is gone.
      editsThisGenerationRef.current = 0;
      track('dispute_draft_discarded', { invoice_id: invoiceId });
    }
  }, [invoiceId, mutateState, revalidateInvoice, track]);

  const sendEmail = useCallback(
    async (attachmentIds?: string[]) => {
      if (!invoiceId) return;
      const draftBefore = state?.currentDraft ?? null;
      try {
        // First update draft with attachment IDs if provided
        if (attachmentIds && attachmentIds.length > 0) {
          await invoicesDisputeDraftUpdate(invoiceId, { attachmentIds });
        }
        // Then send the email
        const { data } = await invoicesDisputeSendCreate(invoiceId);
        await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
        await Promise.all([mutateTimeline(), mutateActions()]);
        revalidateInvoice(); // Sync dispute status to invoice header
        // `agent_generated` reflects the *sent* version, not the
        // draft's original generation flag — so a draft that came
        // from the LLM but was then edited by the user reports
        // `false`. The draft-quality funnel in analytics.md uses this
        // distinction; without the edit counter it would have always
        // been `isAgentGenerated`'s original value.
        track('dispute_email_sent', {
          invoice_id: invoiceId,
          attachment_count: attachmentIds?.length ?? draftBefore?.attachments?.length ?? 0,
          agent_generated:
            (draftBefore?.isAgentGenerated ?? false) && editsThisGenerationRef.current === 0,
        });
        // Sent → reset for the next generation cycle.
        editsThisGenerationRef.current = 0;
      } catch (e) {
        // The dispute viewset returns its message under `error` (with a
        // `send_failed` code + `failedAttachments` when SendGrid rejects the
        // send); DRF's own errors use `detail`. Prefer the specific message so
        // the consultant sees "vendor not contacted", not a generic fallback.
        const errorMessage =
          e instanceof LiftAPIClientError
            ? (() => {
                const data = e.context?.data as
                  | { error?: string; detail?: string; failedAttachments?: string[] }
                  | undefined;
                const base =
                  data?.error ?? data?.detail ?? 'Failed to send dispute email. Please try again.';
                return data?.failedAttachments?.length
                  ? `${base} Missing attachments: ${data.failedAttachments.join(', ')}.`
                  : base;
              })()
            : e instanceof Error
              ? e.message
              : 'Failed to send email';
        // Analytics gets only the bounded error code — the snackbar +
        // Sentry capture the raw text. The explicit annotation pushes
        // the template literal type onto the expression so the call
        // site type-checks against the schema's `http_${number}` |
        // 'unknown_error' shape; without it TS widens the template to
        // plain `string`.
        const errorCode: `http_${number}` | 'unknown_error' =
          e instanceof LiftAPIClientError ? `http_${e.context.status}` : 'unknown_error';
        track('dispute_email_send_failed', {
          invoice_id: invoiceId,
          error_code: errorCode,
        });
        if (e instanceof LiftAPIClientError) {
          throw new Error(errorMessage);
        }
        throw e instanceof Error ? e : new Error('Failed to send email');
      }
    },
    [invoiceId, state, mutateState, mutateTimeline, mutateActions, revalidateInvoice, track],
  );

  const categorizeResponse = useCallback(
    async (timelineEntryId: string, vendorPosition: string, vendorClaimedAmount?: number) => {
      if (!invoiceId) return;
      try {
        // Decimal fields are typed as strings on the generated body, but DRF
        // accepts the numeric value; cast at this boundary (AMP-503).
        const { data } = await invoicesDisputeCategorizeCreate(invoiceId, {
          timelineEntryId,
          vendorPosition,
          vendorClaimedAmount,
        } as unknown as DisputeCategorizeRequest);
        await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
        await mutateActions();
        track('dispute_response_categorized', {
          invoice_id: invoiceId,
          vendor_position: vendorPosition,
        });
      } catch (e) {
        throw e instanceof Error ? e : new Error('Failed to categorize response');
      }
    },
    [invoiceId, mutateState, mutateActions, track],
  );

  const resolve = useCallback(
    async (resolutionType: string, settledAmount: number, notes?: string) => {
      if (!invoiceId) return;
      try {
        // settledAmount is a string on the generated body, but DRF accepts the
        // numeric value; cast at this boundary (AMP-503).
        const { data } = await invoicesDisputeResolveCreate(invoiceId, {
          resolutionType,
          settledAmount,
          notes,
        } as unknown as DisputeResolveRequest);
        await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
        await Promise.all([mutateTimeline(), mutateActions()]);
        revalidateInvoice(); // Sync dispute status to invoice header
        track('dispute_resolved', {
          invoice_id: invoiceId,
          resolution_type: resolutionType,
          had_settled_amount: settledAmount > 0,
        });
      } catch (e) {
        throw e instanceof Error ? e : new Error('Failed to resolve dispute');
      }
    },
    [invoiceId, mutateState, mutateTimeline, mutateActions, revalidateInvoice, track],
  );

  const withdraw = useCallback(
    async (reason?: string) => {
      if (!invoiceId) return;
      try {
        const { data } = await invoicesDisputeWithdrawCreate(
          invoiceId,
          reason ? { reason } : undefined,
        );
        await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
        await Promise.all([mutateTimeline(), mutateActions()]);
        revalidateInvoice(); // Sync dispute status to invoice header
        track('dispute_withdrawn', { invoice_id: invoiceId, had_reason: Boolean(reason) });
      } catch (e) {
        throw e instanceof Error ? e : new Error('Failed to withdraw dispute');
      }
    },
    [invoiceId, mutateState, mutateTimeline, mutateActions, revalidateInvoice, track],
  );

  const reopen = useCallback(async () => {
    if (!invoiceId) return;
    const fromStatus = state?.status ?? 'unknown';
    try {
      // The reopen endpoint takes no body (the old client's `reason` was sent
      // but the backend ignored it), so drop it here (AMP-503).
      const { data } = await invoicesDisputeReopenCreate(invoiceId);
      await mutateState(data as unknown as DisputeAgentState, { revalidate: false });
      await Promise.all([mutateTimeline(), mutateActions()]);
      revalidateInvoice(); // Sync dispute status to invoice header
      track('dispute_reopened', { invoice_id: invoiceId, from_status: fromStatus });
    } catch (e) {
      throw e instanceof Error ? e : new Error('Failed to reopen dispute');
    }
  }, [invoiceId, state, mutateState, mutateTimeline, mutateActions, revalidateInvoice, track]);

  return {
    state: state ?? null,
    timeline: timeline ?? [],
    actions: actions ?? [],
    loading: stateLoading,
    error: stateError
      ? stateError instanceof Error
        ? stateError.message
        : 'Failed to fetch dispute state'
      : null,
    initiateDispute,
    generateDraft,
    updateDraft,
    discardDraft,
    sendEmail,
    categorizeResponse,
    resolve,
    withdraw,
    reopen,
    refreshState: mutateState,
    refreshTimeline: useCallback(() => {
      void mutateTimeline();
      revalidateInvoice();
    }, [mutateTimeline, revalidateInvoice]),
  };
}
